import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { FIRM, TEL_LINK } from "../data/firm";
import { useConsultation } from "../context/ConsultationContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/insights", label: "Insights" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openConsultation } = useConsultation();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-xl transition-shadow duration-300 ${
        scrolled ? "shadow-[0_1px_24px_rgba(15,23,42,0.08)]" : ""
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label="Aniruddha Sonar & Associates - Home">
          <img src="/assets/ca-india-logo.webp" alt="CA India" className="h-9 w-auto" loading="eager" />
          <span className="leading-tight">
            <span className="block font-display text-lg font-bold tracking-tight text-navy sm:text-xl">
              Aniruddha Sonar <span className="text-corpblue">&amp;</span> Associates
            </span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-500">
              Chartered Accountants
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                  isActive ? "text-corpblue" : "text-navy-700 hover:text-navy"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <button
            data-testid="nav-get-consultation-button"
            onClick={() => openConsultation()}
            className="bg-navy px-5 py-2.5 text-[13px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-corpblue"
          >
            Get Consultation
          </button>
        </nav>

        <button
          className="p-2 text-navy lg:hidden"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          data-testid="mobile-menu-toggle"
        >
          {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {menuOpen && (
        <nav className="border-t border-slate-200 bg-white lg:hidden" aria-label="Mobile" data-testid="mobile-menu">
          <div className="space-y-1 px-5 py-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block px-2 py-3 text-sm font-semibold uppercase tracking-[0.14em] ${
                    isActive ? "text-corpblue" : "text-navy-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex gap-3 pt-3">
              <button
                data-testid="mobile-nav-consultation-button"
                onClick={() => openConsultation()}
                className="flex-1 bg-navy px-4 py-3 text-center text-xs font-bold uppercase tracking-widest text-white"
              >
                Get Consultation
              </button>
              <a
                href={TEL_LINK}
                data-testid="mobile-nav-call-link"
                className="flex-1 border border-navy px-4 py-3 text-center text-xs font-bold uppercase tracking-widest text-navy"
              >
                Call
              </a>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};
